import { Camera } from 'lucide-react';
import { Link } from 'react-router-dom';

const COLORS = [
  'bg-sky-100 dark:bg-sky-900/40 text-sky-600',
  'bg-amber-100 dark:bg-amber-900/40 text-amber-600',
  'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600',
  'bg-violet-100 dark:bg-violet-900/40 text-violet-600',
  'bg-rose-100 dark:bg-rose-900/40 text-rose-600',
  'bg-orange-100 dark:bg-orange-900/40 text-orange-600',
];

function colorForUsername(username: string): string {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COLORS[Math.abs(hash) % COLORS.length];
}

const SIZE_CLASSES = {
  sm:  { wrap: 'h-8 w-8',   text: 'text-sm',  badge: 'h-4 w-4 text-[9px] -bottom-1 -right-1' },
  md:  { wrap: 'h-10 w-10', text: 'text-sm',  badge: 'h-4 w-4 text-[9px] -bottom-1 -right-1' },
  lg:  { wrap: 'h-16 w-16', text: 'text-2xl', badge: 'h-6 w-6 text-xs    -bottom-1 -right-1'  },
  xl:  { wrap: 'h-20 w-20', text: 'text-4xl', badge: 'h-7 w-7 text-sm    -bottom-1 -right-1'  },
};

interface UserAvatarProps {
  username: string;
  avatarUrl?: string | null;
  size?: keyof typeof SIZE_CLASSES;
  /** When true, shows a camera overlay on hover for upload UX */
  editable?: boolean;
  onClick?: () => void;
  className?: string;
  /** When provided, shows a level badge on the bottom-right of the avatar */
  level?: number;
  /** When provided, the level badge becomes a link to this href */
  trophiesHref?: string;
}

export default function UserAvatar({
  username,
  avatarUrl,
  size = 'md',
  editable = false,
  onClick,
  className = '',
  level,
  trophiesHref,
}: UserAvatarProps) {
  const { wrap, text, badge } = SIZE_CLASSES[size];
  const color = colorForUsername(username);
  const letter = username?.[0]?.toUpperCase() ?? '?';

  const levelBadge = level != null ? (
    trophiesHref ? (
      <Link
        to={trophiesHref}
        title="View Trophy Case"
        onClick={e => e.stopPropagation()}
        className={`absolute ${badge} rounded-full bg-brand-600 border-2 border-white text-white font-bold flex items-center justify-center shadow-sm hover:bg-brand-700 transition-colors z-10`}
      >
        {level}
      </Link>
    ) : (
      <span
        className={`absolute ${badge} rounded-full bg-brand-600 border-2 border-white text-white font-bold flex items-center justify-center shadow-sm z-10`}
      >
        {level}
      </span>
    )
  ) : null;

  return (
    <div
      className={`${wrap} rounded-full flex-shrink-0 relative ${editable ? 'cursor-pointer' : ''} ${className}`}
      onClick={editable ? onClick : undefined}
    >
      <div className="absolute inset-0 rounded-full overflow-hidden">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={username}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className={`h-full w-full flex items-center justify-center ${color}`}>
            <span className={`font-bold ${text}`}>{letter}</span>
          </div>
        )}

        {editable && (
          <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
            <Camera className="h-5 w-5 text-white" />
          </div>
        )}
      </div>

      {levelBadge}
    </div>
  );
}
